/**
 * sf-enquiry-form
 *
 * Purpose: Shows the contact form's field groups that match the chosen enquiry type
 * (e.g. Wedding shows Event Details, Your Vision, Inspiration) and switches the others off.
 * A switched-off group is `hidden` and `disabled`, so its required fields don't block the
 * form and its values aren't sent in the enquiry email. Also pre-selects the enquiry type
 * from `?type=Wedding` in the URL, and in the theme editor reveals a hidden group when one
 * of its blocks is selected.
 *
 * Why not core: Horizon's contact form has no enquiry type or conditional fields.
 *
 * How: <sf-enquiry-form> custom element (one instance per form, no page scan). Groups carry
 * data-sf-show-for="Wedding, Event"; groups without it are always on. Liquid renders
 * conditional groups hidden + disabled, so without JavaScript a general enquiry still sends.
 *
 * Horizon dependencies: none (theme editor's `shopify:block:select` event only).
 * Tests: tests/sf-enquiry-form.test.mjs (`node --test tests/`).
 */

/** Splits a "Wedding, Event" setting into normalised type names. */
export function parseTypes(value) {
  return String(value ?? '')
    .split(',')
    .map((type) => type.trim().toLowerCase())
    .filter(Boolean);
}

/** A group is active when it has no types, or the selected type is one of them. */
export function isGroupActive(showFor, selected) {
  const types = parseTypes(showFor);
  if (types.length === 0) return true;
  return types.includes(String(selected ?? '').trim().toLowerCase());
}

/** Finds the option matching `type` (case-insensitive) and returns its exact value, or ''. */
export function matchOption(type, options) {
  const wanted = String(type ?? '').trim().toLowerCase();
  if (!wanted) return '';
  return options.find((option) => option.trim().toLowerCase() === wanted) ?? '';
}

/** Reads `?type=` from a query string and matches it against the dropdown options. */
export function typeFromSearch(search, options) {
  return matchOption(new URLSearchParams(search).get('type'), options);
}

/** First option that would reveal a group with this show-for setting, or ''. */
export function firstOptionForGroup(showFor, options) {
  for (const type of parseTypes(showFor)) {
    const match = matchOption(type, options);
    if (match) return match;
  }
  return '';
}

if (typeof HTMLElement !== 'undefined' && typeof customElements !== 'undefined') {
  class SfEnquiryForm extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('[data-sf-enquiry-type]');
      if (!this.select) return;

      this.handleChange = () => this.update();
      this.handleBlockSelect = (event) => this.revealBlock(event.target);
      this.select.addEventListener('change', this.handleChange);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);

      if (!this.select.value) {
        const fromUrl = typeFromSearch(window.location.search, this.optionValues());
        if (fromUrl) this.select.value = fromUrl;
      }
      this.update();
    }

    disconnectedCallback() {
      this.select?.removeEventListener('change', this.handleChange);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    }

    optionValues() {
      return [...this.select.options].map((option) => option.value).filter(Boolean);
    }

    update() {
      for (const group of this.querySelectorAll('[data-sf-show-for]')) {
        const active = isGroupActive(group.dataset.sfShowFor, this.select.value);
        group.hidden = !active;
        group.disabled = !active;
      }
    }

    revealBlock(target) {
      if (!(target instanceof Element) || !this.contains(target)) return;
      const group = target.closest('[data-sf-show-for]');
      if (!group || !group.hidden) return;
      const option = firstOptionForGroup(group.dataset.sfShowFor, this.optionValues());
      if (!option) return;
      this.select.value = option;
      this.update();
    }
  }

  if (!customElements.get('sf-enquiry-form')) customElements.define('sf-enquiry-form', SfEnquiryForm);
}
